import {Component, Input} from "@angular/core";
import {AvdbType} from "../avdb-type";
import {AvdbPricingAircraftBundle} from "../database-pricing/avdb-pricing-aircraft-bundle";
import {AvdbPricingBundle} from "../database-pricing/avdb-pricing-bundle";
import {AvdbPricingBundlePackage} from "../database-pricing/avdb-pricing-bundle-package";
import {AvdbPricingDeviceBundle} from "../database-pricing/avdb-pricing-device-bundle";
import {AvdbPricingDeviceBundleAvdbType} from "../database-pricing/avdb-pricing-device-bundle-avdb-type";
import {AvdbPricingProduct} from "../database-pricing/avdb-pricing-product";
import {Device} from "../device";

@Component({
	selector: "bundle-package-coverage-detail",
	template: `
		<modal *ngIf="visible" (close)="close()">

			<!---------- MODAL HEADER ---------->
			<header class="modal_header">
				<h2>Coverage Details</h2>
			</header>

			<!---------- MODAL BODY ---------->
			<div class="modal_body">

				<!---------- LIST OF BUNDLES ---------->
				<ng-container *ngFor="let bundle of bundlePackage.bundles; let bundleIndex = index;">

					<hr *ngIf="bundleIndex > 0">

					<!---------- BUNDLE - NAME ---------->
					<h4 class="databasepricing_recommendedbundles_info_bundle">
						{{bundle.description}}
						<span *ngIf="getDeviceNameForBundle(bundle)" class="databasepricing_recommendedbundles_info_device">
							- {{getDeviceNameForBundle(bundle)}}
						</span>
					</h4>

					<!---------- BUNDLE - PART NUMBER ---------->
					<p class="coveragedetail_productdetails">Part number: {{bundle.partKey}}</p>
					<p class="coveragedetail_productdetails">Duration: 1 Year</p>

					<!---------- BUNDLE - LIST OF DEVICES ---------->
					<ng-container *ngFor="let device of getDevicesCoveredByBundle(bundle)">

						<h3>{{device.name}}</h3>

						<ul class="coveragedetail_ul">

							<!---------- DEVICE - LIST OF AVDB TYPES ---------->
							<li *ngFor="let avdbType of getAvdbTypesForDeviceAndBundle(device, bundle)">
								{{avdbType.name}}
								<ul class="coveragedetail_ul">

									<!---------- AVDB TYPE - LIST OF PRODUCTS ---------->
									<li class="coveragedetail_product" *ngFor="let product of getProductsFromPartKeys(avdbType.productOptions); let pIndex = index">

										<!---------- PRODUCT - LIST OF SERIES ---------->
										<ng-container *ngFor="let series of product.series; let sIndex = index">
											<span class="coveragedetail_product_name">{{series.region.displayName}}</span>

											<!---------- IF MULTIPLE SERIES ---------->
											<span *ngIf="sIndex < (product.series.length - 1)">
												- AND -
											</span>

											<!---------- SERIES - COVERAGE DETAIL PANEL ---------->
											<coverage-detail-panel [coverageSeries]="series" [coverageProduct]="product"></coverage-detail-panel>

										</ng-container>

										<!---------- MULTIPLE SERIES MESSAGE ---------->
										<small *ngIf="product.series.length > 1">
											<em>Only one of these databases may be installed at a time.</em>
											<br>
										</small>

										<!---------- IF MULTIPLE PRODUCTS ---------->
										<ng-container *ngIf="pIndex < (getProductsFromPartKeys(avdbType.productOptions).length - 1)">
											- OR -
										</ng-container>
									</li>

									<!---------- MULTIPLE PRODUCTS MESSAGE ---------->
									<small *ngIf="getProductsFromPartKeys(avdbType.productOptions).length > 1">
										<em>After purchasing this bundle, you will need to select one of these options.</em>
									</small>
								</ul>
							</li>
						</ul>
					</ng-container>
				</ng-container>

				<!---------- LIST OF DEVICES - FROM ADDITIONAL PRODUCTS ---------->
				<ng-container *ngFor="let device of bundlePackage.getDevicesCoveredByAdditionalProducts()">
					<hr>
					<p>Additional Products:</p>

					<h3>{{device.name}}</h3>

					<ul class="coveragedetail_ul">

						<!---------- LIST OF ADDITIONAL PRODUCTS ---------->
						<li *ngFor="let product of bundlePackage.getAdditionalProductsForDevice(device)">
							{{product.productType.name}}

							<span class="coveragedetail_productdetails">- Part number: {{product.partKey}}</span>

							<ul class="coveragedetail_ul">

								<!---------- ADDITIONAL PRODUCT - LIST OF SERIES ---------->
								<li class="coveragedetail_product" *ngFor="let series of product.series; let sIndex = index">

									<span class="coveragedetail_product_name">{{series.region.displayName}}</span>

									<!---------- IF MULTIPLE SERIES ---------->
									<span *ngIf="sIndex < (product.series.length - 1)">
										- AND -
									</span>

									<!---------- SERIES - NUMBER OF ISSUES ---------->
									<small *ngIf="sIndex === (product.series.length - 1)" class="databasepricing_row_issues">
										{{product.numberOfIssuesDisplay}}
									</small>

									<!---------- SERIES - COVERAGE DETAIL PANEL ---------->
									<coverage-detail-panel [coverageSeries]="series" [coverageProduct]="product"></coverage-detail-panel>

								</li>

								<!---------- MULTIPLE SERIES MESSAGE ---------->
								<small *ngIf="product.series.length > 1">
									<em>Only one of these databases may be installed at a time.</em>
									<br>
								</small>

							</ul>
						</li>

					</ul>
				</ng-container>

			</div>
		</modal>
	`,
})
export class BundlePackageCoverageDetailComponent {
	visible = false;
	bundlePackage: AvdbPricingBundlePackage;

	@Input() devices: Array<Device>;
	@Input() bundleToDevicesMap = new Map<AvdbPricingBundle, Array<Device>>();
	@Input() partKeyProductMap: Map<string, AvdbPricingProduct>;

	open(bundlePackage: AvdbPricingBundlePackage) {
		this.bundlePackage = bundlePackage;
		this.visible = true;
	}

	close() {
		this.visible = false;
	}

	getDeviceNameForBundle(bundle: AvdbPricingBundle): string {
		if (bundle instanceof AvdbPricingDeviceBundle) {
			const deviceID = (bundle as AvdbPricingDeviceBundle).deviceID;
			const device = this.devices.find(device => device.id === deviceID);
			return (device) ? device.name : undefined;
		}
		else {
			return undefined;
		}
	}

	getDevicesCoveredByBundle(bundle: AvdbPricingBundle): Array<Device> {
		const devices = this.bundleToDevicesMap.get(bundle);
		return (devices) ? devices : [];
	}

	getAvdbTypesForDeviceAndBundle(device: Device, bundle: AvdbPricingBundle): Array<AvdbPricingDeviceBundleAvdbType> {
		let avdbTypes: Array<AvdbPricingDeviceBundleAvdbType> = [];
		if (bundle instanceof AvdbPricingAircraftBundle) {
			const deviceModel = (bundle as AvdbPricingAircraftBundle).qualifyingDeviceModels.find(deviceModel => deviceModel.modelID === device.modelID);
			if (deviceModel) {
				avdbTypes = deviceModel.deviceModelAvdbTypes;
			}
		}
		else {
			avdbTypes = (bundle as AvdbPricingDeviceBundle).avdbTypes;
		}
		return _.sortBy(avdbTypes, avdbType => AvdbType.displayOrder(avdbType.id));
	}

	getProductsFromPartKeys(partKeys: Array<string>): Array<AvdbPricingProduct> {
		const products: Array<AvdbPricingProduct> = [];
		for (let partKey of partKeys) {
			const product = this.partKeyProductMap.get(partKey);
			if (product) {
				products.push(product);
			}
		}
		return products;
	}
}
